import { ColorModeBgPropKeys, ColorModeBorderPropKeys } from 'styles/colors';
import { FeatureBlockProps } from 'src/app/molecules/PricingCard/FeatureBlock';
import { ButtonProps } from 'atoms/Button/Button';

export type IPricingCard = {
  planTitle: string;
  supportingText: string;
  price?: number;
  pricePeriodText?: string;
  featureBlocks: FeatureBlockProps[];
  bannerText?: {
    text: string;
    background?: ColorModeBgPropKeys;
  };
  callToAction?: {
    text: string;
    href: string;
    variant?: ButtonProps['variant'];
  };
  background?: ColorModeBgPropKeys;
  cardBorder?: ColorModeBorderPropKeys;
  priceBlockBorder?: ColorModeBorderPropKeys;
};

const createKeyFeaturesBlock = (keyFeatures: string[]): FeatureBlockProps => ({
  headlineProps: {
    type: 'paragraph',
    size: 'md',
    fontWeight: 'font-weight-semibold',
    textAlign: 'left',
    text: 'KEY FEATURES',
  },
  checkItemTextListProps: {
    checkItems: keyFeatures,
    checkItemProps: {
      size: 'sm',
      type: 'line',
      color: 'success',
    },
    gap: 'spacing-xl',
    align: 'flex-start',
    justify: 'center',
    direction: 'column',
    padding: 'spacing-none',
  },
});

const createSimpleCheckFeaturesBlock = (headline: string, features: string[]): FeatureBlockProps => ({
  headlineProps: {
    type: 'paragraph',
    size: 'md',
    fontWeight: 'font-weight-semibold',
    textAlign: 'left',
    text: headline,
  },
  checkItemTextListProps: {
    checkItems: features,
    checkItemProps: {
      size: 'sm',
      type: 'simpleCheck',
      color: 'success',
    },
    gap: 'spacing-md',
    align: 'flex-start',
    justify: 'center',
    direction: 'column',
    padding: 'spacing-none',
  },
});

const limitFeatureBlocksIfNeeded = (blocks: FeatureBlockProps[], limit: boolean): FeatureBlockProps[] => {
  if (!limit) return blocks;

  const firstBlock = blocks[0];
  const features = firstBlock.checkItemTextListProps.checkItems;

  // Only return first block, and limit to 8 items if it has more than 8
  return [
    {
      ...firstBlock,
      checkItemTextListProps: {
        ...firstBlock.checkItemTextListProps,
        checkItems: features.length > 8 ? features.slice(0, 8) : features,
      },
    },
  ];
};

type PriceMode = 'monthly' | 'annual';

type PlanLabel = 'community' | 'courses' | 'business' | 'growth' | 'mighty_pro';

const getPriceByPlanLabel = (planLabel: PlanLabel, priceMode: PriceMode): number => {
  switch (planLabel) {
    case 'community':
      return priceMode === 'monthly' ? 59 : 49;
    case 'courses':
      return priceMode === 'monthly' ? 129 : 109;
    case 'business':
      return priceMode === 'monthly' ? 229 : 189;
    case 'growth':
      return priceMode === 'monthly' ? 430 : 360;
    case 'mighty_pro':
      return null;
  }
};

const usePricingCards = (priceMode: PriceMode): IPricingCard[] => {
  return [
    {
      planTitle: 'The Community Plan',
      supportingText: 'Start a community or move one over from a social media group or a chat app.',
      price: getPriceByPlanLabel('community', priceMode),
      featureBlocks: [createKeyFeaturesBlock(['Member Profiles', 'Streaks', 'Chat, Feed, & Events'])],
    },
    {
      planTitle: 'The Courses Plan',
      supportingText: 'Add courses, challenges, and resource libraries to a community or membership.',
      price: getPriceByPlanLabel('courses', priceMode),
      featureBlocks: [createKeyFeaturesBlock(['Courses & Challenges', 'Basic Automations', 'Gamification'])],
    },
    {
      planTitle: 'The Business Plan',
      supportingText:
        'Integrate easily with other tools and support your growth with automatic polls & questions and other extras.',
      price: getPriceByPlanLabel('business', priceMode),
      featureBlocks: [createKeyFeaturesBlock(['10 Custom Fields', 'Intermediate Automations'])],
      bannerText: {
        text: 'Your Free Trial',
      },
    },
    {
      planTitle: 'The Growth Plan',
      supportingText: 'Grow your revenue with dedicated services and support and our most advanced automations.',
      price: getPriceByPlanLabel('growth', priceMode),
      featureBlocks: [createKeyFeaturesBlock(['More Livestream Hosts', 'Advanced Automations', '30 Custom Fields'])],
    },
  ];
};

export const usePricingCardsWithPro = (priceMode: PriceMode): IPricingCard[] => {
  return [
    {
      planTitle: 'The Courses Plan',
      supportingText: 'Start a community or move one over from a social media group or a chat app.',
      price: getPriceByPlanLabel('courses', priceMode),
      featureBlocks: [
        createKeyFeaturesBlock(['Courses & Challenges', 'Gamification', 'Chat, Feed, & Events', 'Basic Automations']),
      ],
    },
    {
      planTitle: 'The Business Plan',
      supportingText:
        'Integrate easily with other tools and support your growth with automatic polls & questions and other extras.',
      price: getPriceByPlanLabel('business', priceMode),
      featureBlocks: [createKeyFeaturesBlock(['10 Custom Fields', 'Intermediate Automations'])],
      bannerText: {
        text: 'Your Free Trial',
      },
    },
    {
      planTitle: 'The Growth Plan',
      supportingText: 'Grow your revenue with dedicated services and support and our most advanced automations.',
      price: getPriceByPlanLabel('growth', priceMode),
      featureBlocks: [createKeyFeaturesBlock(['More Livestream Hosts', 'Advanced Automations', '30 Custom Fields'])],
    },
    {
      planTitle: 'Mighty Pro',
      supportingText: 'Create a premium experience with your own custom branded apps and setup and strategy services.',
      featureBlocks: [
        createKeyFeaturesBlock(['Custom Branded Apps', 'Advanced Data Reports', 'Unlimited Custom Fields']),
      ],
      callToAction: {
        text: 'Get a Custom Demo',
        href: '/request-a-call-form',
      },
      bannerText: {
        text: 'Branded Apps',
        background: 'bg-primary-solid',
      },
    },
  ];
};

export const usePricingCardsCoursesAndBusinessOnly = (): IPricingCard[] => {
  return [
    {
      planTitle: 'The Courses Plan',
      supportingText:
        'Bring your courses and community together in one platform built to keep your members engaged year-round.',
      price: getPriceByPlanLabel('courses', 'annual'),
      pricePeriodText: 'per month (paid annually)',
      featureBlocks: [createKeyFeaturesBlock(['Courses & Challenges', 'Basic Automations'])],
      callToAction: {
        text: 'Choose Courses Plan',
        href: 'https://cohost.mn.co/?plan=courses-2024-yearly',
      },
    },
    {
      planTitle: 'The Business plan',
      supportingText:
        'Everything included in the Courses Plan PLUS powerful tools to automate and customize your member experience.',
      price: getPriceByPlanLabel('business', 'annual'),
      pricePeriodText: 'per month (paid annually)',
      featureBlocks: [createKeyFeaturesBlock(['Unlimited Custom Profile Fields', 'Intermediate Automations'])],
      callToAction: {
        text: 'Choose Business Plan',
        href: 'https://cohost.mn.co/?plan=business-2024-yearly',
      },
    },
  ];
};

export const usePricingCardsWithFocusedCourses = (
  priceMode: PriceMode,
  limitedFeatureBlocks = false,
): IPricingCard[] => {
  // Courses Plan feature blocks
  const coursesFeatureBlocks = limitFeatureBlocksIfNeeded(
    [
      createSimpleCheckFeaturesBlock('Core Features', [
        'Unlimited Hosts, Moderators & Members',
        'Multi-Feature Spaces',
        'Courses & Challenges',
        'Activity Feed & Chat',
        'Polls & Questions',
        'Events',
        'Paid Memberships',
        'Installments & Intervals',
        'Promo Codes',
        'Custom Branding',
        'Direct Messages',
        'Voice Notes',
        'Member Profiles',
        'Badges & Tags',
        'Member Explorer',
        'Gamification',
        'Mighty Insights™',
        'Chat with AI Cohost',
        'Basic Automations',
        'Limited Promotions',
        'Landing Page',
        'Livestreaming',
      ]),
    ],
    limitedFeatureBlocks,
  );

  // Business Plan feature blocks
  const businessFeatureBlocks = limitFeatureBlocksIfNeeded(
    [
      createSimpleCheckFeaturesBlock('Everything in Courses, plus:', [
        'Intermediate Automations',
        'Automate Interest-Based Groups',
        'AI Question Generator',
        'More Badges & Tags',
        'Charge for Events',
      ]),
      createSimpleCheckFeaturesBlock('Integrations', [
        'Basic API Access',
        'Zapier Integration',
        'Kit Integration',
        'Affiliate Integrations',
      ]),
    ],
    limitedFeatureBlocks,
  );

  // Growth Plan feature blocks
  const growthFeatureBlocks = limitFeatureBlocksIfNeeded(
    [
      createSimpleCheckFeaturesBlock('Everything in Business, plus:', [
        'Advanced Automations',
        'AI Automations',
        'AI Moderation',
        'Team Livestreaming',
        'Member Video Upload',
        'Multiple Landing Pages',
      ]),
      createSimpleCheckFeaturesBlock('Integrations', [
        'Custom Single-Sign On (SSO)',
        'Higher API Limits',
        'White-Label Emails',
      ]),
      createSimpleCheckFeaturesBlock('Strategy & Support', [
        'Private Growth Membership',
        'Exclusive Growth Workshops',
        'Weekly Masterminds',
      ]),
    ],
    limitedFeatureBlocks,
  );

  // Mighty Pro feature blocks
  const mightyProFeatureBlocks = limitFeatureBlocksIfNeeded(
    [
      createSimpleCheckFeaturesBlock('Everything in Growth, plus:', [
        'Branded Native iOS & Android Apps',
        'Branded Mobile Notifications',
        '1080p Livestreaming',
        'Highest Livestreaming Limits',
        'Unlimited Custom Profile Fields',
        'Extra Promotions',
        'Extra Landing Pages',
        'Max Storage',
      ]),
      createSimpleCheckFeaturesBlock('Integrations', [
        'Highest API Limits',
        'Custom Reporting',
        'Custom Embed Options',
      ]),
      createSimpleCheckFeaturesBlock('Strategy & Support', [
        'Dedicated Launch Team',
        'Dedicated CSM',
        'Personalized Community and Offer Strategy',
        'Prioritized migration service',
        'Access to Mighty Design Studio',
        'Technical Support',
      ]),
    ],
    limitedFeatureBlocks,
  );

  return [
    {
      planTitle: 'The Courses Plan',
      supportingText: 'Everything you need to launch & validate your idea.',
      price: getPriceByPlanLabel('courses', priceMode),
      featureBlocks: coursesFeatureBlocks,
      bannerText: {
        text: 'Best Value',
      },
      callToAction: {
        text: 'Start Your Free Trial',
        href: 'https://cohost.mn.co?plan=free-trial',
        variant: 'secondary',
      },
      background: 'bg-brand-primary',
      cardBorder: 'border-brand',
      priceBlockBorder: 'border-brand',
    },
    {
      planTitle: 'The Business Plan',
      supportingText: 'Scale faster with more personalization and automation.',
      price: getPriceByPlanLabel('business', priceMode),
      featureBlocks: businessFeatureBlocks,
      callToAction: {
        text: 'Start Your Free Trial',
        href: 'https://cohost.mn.co?plan=free-trial',
        variant: 'secondary',
      },
    },
    {
      planTitle: 'The Growth Plan',
      supportingText: 'More support, faster community growth, and you keep more of your profit.',
      price: getPriceByPlanLabel('growth', priceMode),
      featureBlocks: growthFeatureBlocks,
      callToAction: {
        text: 'Start Your Free Trial',
        href: 'https://cohost.mn.co?plan=free-trial',
        variant: 'secondary',
      },
    },
    {
      planTitle: 'Mighty Pro',
      supportingText: 'Guarantee extraordinary results with your own branded apps.',
      featureBlocks: mightyProFeatureBlocks,
      callToAction: {
        text: 'Get a Custom Demo',
        href: '/request-a-call-form',
      },
    },
  ];
};

export default usePricingCards;
